import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, Plus, Copy, Trash2, Edit, Package, Zap, Truck, Link2, Sparkles, ExternalLink } from 'lucide-react';
import { adminDealsAPI } from '../../lib/api';

const STATUS_LABELS = {
  pending_payment: '⏳ Paiement en attente',
  declared: 'Déclarée', in_transit: 'En transit', received: 'Reçue',
  assigned_pallet: 'Affectée palette', shipped: 'Expédiée',
  delivered: 'Livrée', refused: 'Refusée', cancelled: 'Annulée',
};

// Photo thumbnail with always-visible delete + broken image placeholder
function PhotoThumb({ url, index, onRemove }) {
  const [broken, setBroken] = useState(false);
  // Reset broken state whenever the underlying URL changes (prevents state leak on reorder/delete)
  React.useEffect(() => { setBroken(false); }, [url]);
  return (
    <div className="relative border rounded overflow-hidden bg-slate-100" data-testid={`prod-photo-${index}`}>
      {broken ? (
        <div className="w-full h-24 flex flex-col items-center justify-center text-center px-1">
          <span className="text-2xl">🚫</span>
          <span className="text-[10px] text-slate-500 mt-1">Image inaccessible</span>
        </div>
      ) : (
        <img src={url} alt="" className="w-full h-24 object-cover" onError={() => setBroken(true)} />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm shadow-md font-bold"
        title="Supprimer cette photo"
        data-testid={`prod-photo-remove-${index}`}
      >✕</button>
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate font-mono">{url.slice(-30)}</div>
    </div>
  );
}

const STATUS_COLORS = {
  pending_payment: 'bg-orange-500',
  declared: 'bg-slate-500', in_transit: 'bg-blue-500', received: 'bg-green-600',
  assigned_pallet: 'bg-indigo-500', shipped: 'bg-purple-500',
  delivered: 'bg-emerald-600', refused: 'bg-red-500', cancelled: 'bg-slate-400',
};

export default function DealsAdmin() {
  const [meta, setMeta] = useState({ destinations: [], categories: [] });
  useEffect(() => { adminDealsAPI.listCategories().then(r => adminDealsAPI.listProducts().then(() => {
    // load meta via public route via direct fetch
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals-meta/destinations`)
      .then(res => res.json()).then(setMeta);
  })); }, []);
  return (
    <Tabs defaultValue="dashboard" className="space-y-4">
      <TabsList className="bg-white border">
        <TabsTrigger value="dashboard" data-testid="deals-admin-tab-dash">📊 Dashboard</TabsTrigger>
        <TabsTrigger value="deals" data-testid="deals-admin-tab-deals"><Zap className="w-4 h-4 mr-1" /> Opérations</TabsTrigger>
        <TabsTrigger value="products" data-testid="deals-admin-tab-products"><Package className="w-4 h-4 mr-1" /> Produits</TabsTrigger>
        <TabsTrigger value="orders" data-testid="deals-admin-tab-orders"><Truck className="w-4 h-4 mr-1" /> Réceptions</TabsTrigger>
        <TabsTrigger value="pallets" data-testid="deals-admin-tab-pallets">🪵 Palettes</TabsTrigger>
        <TabsTrigger value="categories" data-testid="deals-admin-tab-cats">🏷️ Catégories</TabsTrigger>
        <TabsTrigger value="waitlist" data-testid="deals-admin-tab-wait">📮 Liste d&apos;attente</TabsTrigger>
      </TabsList>

      <TabsContent value="dashboard"><DashboardTab /></TabsContent>
      <TabsContent value="deals"><DealsTab meta={meta} /></TabsContent>
      <TabsContent value="products"><ProductsTab meta={meta} /></TabsContent>
      <TabsContent value="orders"><OrdersTab /></TabsContent>
      <TabsContent value="pallets"><PalletsTab /></TabsContent>
      <TabsContent value="categories"><CategoriesTab /></TabsContent>
      <TabsContent value="waitlist"><WaitlistTab /></TabsContent>
    </Tabs>
  );
}

// -------------------- DASHBOARD TAB --------------------
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [profitability, setProfitability] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([adminDealsAPI.stats(), adminDealsAPI.listDeals()]);
        setStats(s.data); setDeals(d.data);
        const openDeal = d.data.find(x => x.status === 'open');
        if (openDeal) setSelectedDealId(openDeal.id);
      } catch { toast.error('Erreur stats'); }
      setLoading(false);
    })();
  }, []);
  useEffect(() => {
    if (!selectedDealId) return;
    adminDealsAPI.profitability(selectedDealId).then(r => setProfitability(r.data)).catch(() => setProfitability(null));
  }, [selectedDealId]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-amber-600" /></div>;
  if (!stats) return <div className="text-center py-16 text-slate-500">Pas de données</div>;

  const maxDaily = Math.max(1, ...stats.daily_received_30d.map(d => d.received));

  return (
    <div className="space-y-6">
      {/* Today */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold">Aujourd&apos;hui</h3>
          <span className="text-xs text-slate-400">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day:'numeric', month:'long' })}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BigStat label="Intéressés" value={stats.today.interests} icon="👀" accent="slate" />
          <BigStat label="Commandes déclarées" value={stats.today.declared} icon="🛒" accent="amber" />
          <BigStat label="Commandes reçues" value={stats.today.received} icon="📦" accent="green" />
        </div>
      </div>

      {/* Global */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Vue d&apos;ensemble</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SmallStat label="Opérations actives" value={stats.global.active_deals} />
          <SmallStat label="Produits actifs" value={stats.global.active_products} />
          <SmallStat label="Total intéressés" value={stats.global.total_interests} />
          <SmallStat label="Total déclarées" value={stats.global.total_declared} />
          <SmallStat label="Total reçues" value={stats.global.total_received} highlight />
        </div>
      </div>

      {/* Chart + Pallets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Commandes reçues</h3>
              <p className="text-xs text-slate-500">30 derniers jours</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-slate-900">{stats.daily_received_30d.reduce((s, d) => s + d.received, 0)}</div>
              <div className="text-xs text-slate-500">sur la période</div>
            </div>
          </div>
          <div className="flex items-end gap-1 h-32">
            {stats.daily_received_30d.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center group relative">
                <div
                  className="w-full bg-gradient-to-t from-amber-500 to-amber-300 rounded-t-sm hover:from-amber-600 hover:to-amber-400 transition-colors relative"
                  style={{ height: `${(d.received / maxDaily) * 100}%`, minHeight: d.received > 0 ? '4px' : '1px' }}
                  title={`${d.date}: ${d.received}`}
                >
                  {d.received > 0 && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {d.received}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-2">
            <span>{stats.daily_received_30d[0]?.date.slice(5)}</span>
            <span>Aujourd&apos;hui</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 mb-4">Palettes</h3>
          <div className="space-y-3">
            <PalletStat label="En remplissage" value={stats.pallets.filling} color="green" />
            <PalletStat label="Complètes" value={stats.pallets.full} color="amber" />
            <PalletStat label="Expédiées" value={stats.pallets.shipped} color="slate" />
          </div>
        </div>
      </div>

      {/* Top products */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">🔥 Top 5 produits les plus demandés</h3>
        {stats.top_products.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">Aucun produit encore</p>
        ) : (
          <div className="space-y-2">
            {stats.top_products.map((p, i) => (
              <div key={p.slug} className="flex items-center gap-3 py-2">
                <div className="w-6 text-slate-400 font-bold text-sm text-center">{i + 1}</div>
                {p.photos?.[0] ? (
                  <img src={p.photos[0]} alt="" className="w-10 h-10 rounded object-cover" />
                ) : <div className="w-10 h-10 rounded bg-slate-100" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.brand}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-black text-slate-900">👀 {p.interested_count}</div>
                  <div className="text-xs text-green-700 font-semibold">📦 {p.received_orders_count} reçues</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profitability simulator */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-900">💰 Simulateur de rentabilité</h3>
          <select
            value={selectedDealId}
            onChange={(e) => setSelectedDealId(e.target.value)}
            className="px-3 py-1.5 border rounded-md text-sm"
            data-testid="prof-deal-select"
          >
            <option value="">— Choisir un deal —</option>
            {deals.map(d => <option key={d.id} value={d.id}>{d.destination?.flag} {d.name}</option>)}
          </select>
        </div>
        {!profitability ? (
          <p className="text-sm text-slate-500 text-center py-6">Sélectionnez un deal pour voir la rentabilité</p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3 mb-5">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs text-slate-500 uppercase font-bold mb-1">Chiffre d&apos;affaires</div>
                <div className="text-2xl font-black text-slate-900" style={{fontFamily:'Chivo, sans-serif'}}>{profitability.total_revenue.toFixed(2)} €</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs text-slate-500 uppercase font-bold mb-1">Coût total (transport + 🛃)</div>
                <div className="text-2xl font-black text-slate-900" style={{fontFamily:'Chivo, sans-serif'}}>{profitability.total_cost.toFixed(2)} €</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs text-slate-500 uppercase font-bold mb-1">🛃 Douane</div>
                <div className="text-2xl font-black text-slate-900" style={{fontFamily:'Chivo, sans-serif'}}>{(profitability.total_customs || 0).toFixed(2)} €</div>
              </div>
              <div className={`rounded-xl p-4 ${profitability.total_margin >= 0 ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <div className={`text-xs uppercase font-bold mb-1 ${profitability.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>Marge nette</div>
                <div className={`text-2xl font-black ${profitability.total_margin >= 0 ? 'text-green-900' : 'text-red-900'}`} style={{fontFamily:'Chivo, sans-serif'}}>
                  {profitability.total_margin >= 0 ? '+' : ''}{profitability.total_margin.toFixed(2)} €
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {profitability.products.map((row, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b last:border-b-0">
                  {row.product?.photos?.[0] ? (
                    <img src={row.product.photos[0]} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : <div className="w-10 h-10 rounded bg-slate-100" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{row.product?.title}</div>
                    <div className="text-xs text-slate-500">
                      {row.received_orders_count} reçues × {row.unit_price.toFixed(2)}€ − transport {row.unit_cost.toFixed(2)}€
                      {row.customs_fee > 0 && <> − 🛃 {row.customs_fee.toFixed(2)}€</>}
                      {' '}= marge {row.margin_per_unit.toFixed(2)}€/u
                      {row.ism_margin_target > 0 && (
                        <span className={`ml-1 ${row.margin_per_unit >= row.ism_margin_target ? 'text-green-700' : 'text-orange-600'}`}>
                          (cible 💼 {row.ism_margin_target.toFixed(2)}€)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-black ${row.total_margin >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {row.total_margin >= 0 ? '+' : ''}{row.total_margin.toFixed(2)} €
                    </div>
                    <div className="text-xs text-slate-500">
                      {row.is_profitable ? (
                        <span className="text-green-600 font-semibold">💰 rentable</span>
                      ) : (
                        <span className="text-orange-600">💸 {row.orders_to_break_even} pour rentabilité</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BigStat({ label, value, icon, accent }) {
  const accents = {
    slate: 'from-slate-50 to-slate-100 text-slate-900',
    amber: 'from-amber-50 to-orange-50 text-amber-900',
    green: 'from-green-50 to-emerald-50 text-green-900',
  };
  return (
    <div className={`bg-gradient-to-br ${accents[accent]} rounded-2xl border border-slate-200 p-6`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-4xl font-black" style={{fontFamily: 'Chivo, sans-serif'}}>{value}</div>
    </div>
  );
}
function SmallStat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
      <div className={`text-2xl font-black ${highlight ? 'text-green-900' : 'text-slate-900'}`} style={{fontFamily: 'Chivo, sans-serif'}}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
function PalletStat({ label, value, color }) {
  const colors = { green: 'bg-green-500', amber: 'bg-amber-500', slate: 'bg-slate-400' };
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${colors[color]}`} />
        <span className="text-sm text-slate-600">{label}</span>
      </div>
      <span className="text-lg font-black text-slate-900">{value}</span>
    </div>
  );
}

// -------------------- DEALS TAB --------------------
function DealsTab({ meta }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [attachTarget, setAttachTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await adminDealsAPI.listDeals(); setDeals(data); } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const seedSample = async () => {
    try { await adminDealsAPI.seedSample(); toast.success('Exemple Martinique créé'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const duplicate = async (id) => {
    try { await adminDealsAPI.duplicateDeal(id); toast.success('Deal dupliqué'); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };
  const remove = async (id) => {
    if (!window.confirm('Archiver ce deal ?')) return;
    try { await adminDealsAPI.deleteDeal(id); toast.success('Archivé'); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };
  const publish = async (d) => {
    if (!window.confirm(`Publier le deal « ${d.name} » ? Il sera visible sur le site.`)) return;
    try { await adminDealsAPI.publishDeal(d.id); toast.success('Deal publié 🚀'); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };
  const unpublish = async (d) => {
    if (!window.confirm('Repasser en brouillon ? Le deal ne sera plus visible sur le site.')) return;
    try { await adminDealsAPI.unpublishDeal(d.id); toast.success('Repassé en brouillon'); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Opérations (Deals)</h3>
        <div className="flex gap-2">
          <Button onClick={seedSample} variant="outline" size="sm" data-testid="deals-seed-btn"><Sparkles className="w-4 h-4 mr-1" /> Exemple</Button>
          <Button onClick={() => setEditing({})} className="bg-amber-500 hover:bg-amber-600 text-slate-900" size="sm" data-testid="deals-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau deal</Button>
        </div>
      </div>

      {loading ? <Loader2 className="animate-spin" /> : deals.length === 0 ? (
        <div className="text-center py-8 text-slate-500">Aucune opération. Cliquez « Exemple » pour créer un deal test.</div>
      ) : (
        <div className="space-y-3">
          {deals.map(d => (
            <div key={d.id} className="border rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`deal-row-${d.slug}`}>
              <div className="text-3xl">{d.destination?.flag}</div>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-bold">{d.name}</span>
                  <Badge className={d.status === 'open' ? 'bg-green-600' : d.status === 'closed' ? 'bg-slate-500' : d.status === 'archived' ? 'bg-red-500' : 'bg-yellow-500'}>{d.status}</Badge>
                </div>
                <div className="text-xs text-slate-500">/{d.slug} · Clôture : {d.close_at ? new Date(d.close_at).toLocaleDateString('fr-FR') : '—'}</div>
                <div className="flex gap-3 text-xs mt-1 text-slate-600">
                  <span>📦 {d.products_count} produits</span>
                  <span>👀 {d.interests_count}</span>
                  <span>🛒 {d.declared_orders_count}</span>
                  <span className="text-green-700 font-semibold">📦 {d.received_orders_count} reçues</span>
                  <span>🪵 {d.pallets_count} palettes</span>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setAttachTarget(d)} data-testid={`deal-products-${d.slug}`}><Link2 className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(d)}><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(d.id)}><Copy className="w-3.5 h-3.5" /></Button>
                {d.status === 'draft' && (
                  <>
                    <a href={`/deals/${d.slug}?preview=1`} target="_blank" rel="noreferrer" title="Prévisualiser (non publié)">
                      <Button size="sm" variant="outline">👁️</Button>
                    </a>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => publish(d)} data-testid={`deal-publish-${d.slug}`}>🚀 Publier</Button>
                  </>
                )}
                {d.status === 'open' && (
                  <Button size="sm" variant="outline" onClick={() => unpublish(d)} title="Repasser en brouillon">📥</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => remove(d.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <DealForm value={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {attachTarget && <DealProductsManager deal={attachTarget} onClose={() => { setAttachTarget(null); load(); }} />}
    </div>
  );
}

function DealForm({ value, meta, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: value.name || '', destination_code: value.destination_code || 'MQ',
    description: value.description || '', banner_url: value.banner_url || '',
    open_at: value.open_at?.split('T')[0] || '', close_at: value.close_at?.split('T')[0] || '',
    estimated_departure: value.estimated_departure || '', status: value.status || 'draft',
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.open_at) payload.open_at = new Date(payload.open_at).toISOString();
      if (payload.close_at) payload.close_at = new Date(payload.close_at + 'T23:59:59').toISOString();
      if (value.id) await adminDealsAPI.updateDeal(value.id, payload);
      else await adminDealsAPI.createDeal(payload);
      toast.success('Enregistré');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title={value.id ? 'Modifier le deal' : 'Nouveau deal'} testId="deal-form">
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="deal-form-name" /></div>
        <div><Label>Destination *</Label>
          <select value={form.destination_code} onChange={e => setForm({...form, destination_code: e.target.value})} className="w-full px-3 py-2 border rounded-md" data-testid="deal-form-dest">
            {meta.destinations?.map(d => <option key={d.code} value={d.code}>{d.flag} {d.name}</option>)}
          </select>
        </div>
        <div><Label>Description</Label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full px-3 py-2 border rounded-md" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Ouverture</Label><Input type="date" value={form.open_at} onChange={e => setForm({...form, open_at: e.target.value})} /></div>
          <div><Label>Clôture</Label><Input type="date" value={form.close_at} onChange={e => setForm({...form, close_at: e.target.value})} /></div>
        </div>
        <div><Label>Départ estimé</Label><Input value={form.estimated_departure} onChange={e => setForm({...form, estimated_departure: e.target.value})} placeholder="ex: 2026-09-25 ou Fin septembre" /></div>
        <div><Label>Bannière (URL image)</Label><Input value={form.banner_url} onChange={e => setForm({...form, banner_url: e.target.value})} /></div>
        <div><Label>Statut</Label>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2 border rounded-md">
            <option value="draft">Brouillon</option><option value="open">Ouvert</option><option value="closed">Fermé</option>
            <option value="shipped">Expédié</option><option value="archived">Archivé</option>
          </select>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="deal-form-submit">
          {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Enregistrer'}
        </Button>
      </form>
    </Modal>
  );
}

function DealProductsManager({ deal, onClose }) {
  const [dps, setDps] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [fixedPrice, setFixedPrice] = useState('24.90');
  const [breakEven, setBreakEven] = useState('10');
  const [minOrders, setMinOrders] = useState(0);
  const [unitCost, setUnitCost] = useState(0);
  const [customsFee, setCustomsFee] = useState(0);
  const [ismMargin, setIsmMargin] = useState('5.00');

  const load = async () => {
    setLoading(true);
    try {
      const [dpr, pr] = await Promise.all([adminDealsAPI.listDealProducts(deal.id), adminDealsAPI.listProducts()]);
      setDps(dpr.data); setProducts(pr.data.filter(p => p.active !== false));
    } catch { toast.error('Erreur'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [deal.id]);

  const attach = async () => {
    if (!selectedProduct) return toast.error('Sélectionnez un produit');
    setAttaching(true);
    try {
      await adminDealsAPI.attachProduct(deal.id, {
        product_id: selectedProduct,
        pricing_mode: 'fixed',
        fixed_price: parseFloat(fixedPrice) || 0,
        break_even_orders: parseInt(breakEven) || 0,
        min_orders_to_ship: parseInt(minOrders) || 0,
        unit_cost: parseFloat(unitCost) || 0,
        customs_fee: parseFloat(customsFee) || 0,
        ism_margin: parseFloat(ismMargin) || 0,
      });
      toast.success('Produit attaché'); setSelectedProduct(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setAttaching(false);
  };

  const editPrice = async (dp) => {
    const price = prompt('Prix de livraison fixe (€)', dp.fixed_price ?? dp.current_price ?? 0);
    if (price === null) return;
    const be = prompt('Nombre de commandes pour rentabilité', dp.break_even_orders ?? 0);
    if (be === null) return;
    try {
      await adminDealsAPI.updateDealProduct(deal.id, dp.id, {
        pricing_mode: 'fixed',
        fixed_price: parseFloat(price) || 0,
        break_even_orders: parseInt(be) || 0,
      });
      toast.success('Prix mis à jour'); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const editSafety = async (dp) => {
    const min = prompt('Seuil minimum de commandes pour expédier (0 = pas de minimum)', dp.min_orders_to_ship || 0);
    if (min === null) return;
    const cost = prompt('Coût unitaire transport ISM (€)', dp.unit_cost || 0);
    if (cost === null) return;
    const customs = prompt('Frais de douane par unité (€, TVA/Octroi/mer)', dp.customs_fee || 0);
    if (customs === null) return;
    const margin = prompt('Marge ISM cible par unité (€) — invisible du client', dp.ism_margin || 0);
    if (margin === null) return;
    try {
      await adminDealsAPI.updateDealProduct(deal.id, dp.id, {
        min_orders_to_ship: parseInt(min) || 0,
        unit_cost: parseFloat(cost) || 0,
        customs_fee: parseFloat(customs) || 0,
        ism_margin: parseFloat(margin) || 0,
      });
      toast.success('Seuils mis à jour'); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Auto-calcul du prix client à partir de coût + douane + marge ISM
  const recomputedPrice = (
    (parseFloat(unitCost) || 0) +
    (parseFloat(customsFee) || 0) +
    (parseFloat(ismMargin) || 0)
  );
  const autoFillPrice = () => {
    setFixedPrice(recomputedPrice.toFixed(2));
    toast.success(`Prix client calculé : ${recomputedPrice.toFixed(2)} €`);
  };

  const [calcOpen, setCalcOpen] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [calcForm, setCalcForm] = useState({
    item_length_cm: '', item_width_cm: '', item_height_cm: '', item_weight_kg: '',
    pallet_length_cm: 120, pallet_width_cm: 80, pallet_max_height_cm: 180, pallet_max_weight_kg: 600,
    pallet_total_cost: '',
  });
  const runCalc = async () => {
    try {
      const payload = Object.fromEntries(Object.entries(calcForm).map(([k, v]) => [k, parseFloat(v) || 0]));
      const { data } = await adminDealsAPI.costCalculator(payload);
      setCalcResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur calcul'); }
  };
  const applyCalcToInput = () => {
    if (calcResult) { setUnitCost(calcResult.cost_per_item); setCalcOpen(false); toast.success('Coût unitaire pré-rempli'); }
  };
  const prefillCalcFromSelected = () => {
    const p = products.find(x => x.id === selectedProduct);
    if (p) {
      setCalcForm(f => ({ ...f,
        item_length_cm: p.est_length_cm ?? '', item_width_cm: p.est_width_cm ?? '',
        item_height_cm: p.est_height_cm ?? '', item_weight_kg: p.est_weight_kg ?? '',
      }));
    }
    setCalcOpen(true);
  };

  const detach = async (dpId) => {
    if (!window.confirm('Retirer ce produit du deal ?')) return;
    try { await adminDealsAPI.detachProduct(deal.id, dpId); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };

  const editTiers = async (dp) => {
    // Legacy tier editor kept for backwards compat with existing tiered deal_products
    const str = prompt(
      'Paliers (format: min-max:prix / min+:prix). Laissez vide pour ne pas modifier.',
      (dp.tiers || []).map(t => `${t.min_orders}-${t.max_orders ?? '+'}:${t.price}`).join(' / ')
    );
    if (!str) return;
    try {
      const tiers = str.split('/').map(part => {
        const [range, price] = part.trim().split(':');
        const [minStr, maxStr] = range.trim().replace('+', '-null').split('-');
        return { min_orders: parseInt(minStr) || 0, max_orders: maxStr === 'null' || maxStr === undefined ? null : parseInt(maxStr), price: parseFloat(price) };
      }).filter(t => !isNaN(t.price));
      await adminDealsAPI.updateDealProduct(deal.id, dp.id, { pricing_mode: 'tiers', tiers });
      toast.success('Paliers mis à jour'); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleFeatured = async (dp) => {
    try { await adminDealsAPI.updateDealProduct(deal.id, dp.id, { featured: !dp.featured }); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };

  const notAttached = products.filter(p => !dps.find(dp => dp.product?.id === p.id));

  return (
    <Modal onClose={onClose} title={`Produits — ${deal.name}`} testId="deal-products" wide>
      {loading ? <Loader2 className="animate-spin" /> : (
        <>
          {/* Attach */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="text-sm font-semibold mb-2">Ajouter un produit</div>
            <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)} className="w-full px-3 py-2 border rounded-md mb-2" data-testid="dp-select-product">
              <option value="">— Choisir un produit —</option>
              {notAttached.map(p => <option key={p.id} value={p.id}>{p.brand} — {p.title}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold">Prix livraison (€) *</Label>
                <Input type="number" step="0.01" min="0" placeholder="ex: 24.90" value={fixedPrice} onChange={e => setFixedPrice(e.target.value)} data-testid="dp-price-input" />
              </div>
              <div>
                <Label className="text-xs font-bold">Rentable dès X reçues</Label>
                <Input type="number" min="0" placeholder="ex: 10" value={breakEven} onChange={e => setBreakEven(e.target.value)} data-testid="dp-be-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-xs">Seuil min. (protection perte)</Label>
                <Input type="number" min="0" placeholder="ex: 8" value={minOrders} onChange={e => setMinOrders(e.target.value)} data-testid="dp-min-input" />
              </div>
              <div>
                <Label className="text-xs">Coût unitaire ISM (€)</Label>
                <div className="flex gap-1">
                  <Input type="number" step="0.1" min="0" placeholder="ex: 18" value={unitCost} onChange={e => setUnitCost(e.target.value)} data-testid="dp-cost-input" />
                  <Button type="button" size="sm" variant="outline" onClick={prefillCalcFromSelected} title="Calculer selon volume+poids" data-testid="dp-calc-btn">🧮</Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 mt-2">
              <div>
                <Label className="text-xs">🛃 Frais de douane par unité (€) — TVA / Octroi de mer</Label>
                <Input type="number" step="0.01" min="0" placeholder="ex: 3.50" value={customsFee} onChange={e => setCustomsFee(e.target.value)} data-testid="dp-customs-input" />
              </div>
              <div>
                <Label className="text-xs">💼 Marge ISM cible par unité (€) — <span className="text-slate-500 italic">invisible du client</span></Label>
                <div className="flex gap-1">
                  <Input type="number" step="0.01" min="0" placeholder="ex: 5.00" value={ismMargin} onChange={e => setIsmMargin(e.target.value)} data-testid="dp-margin-input" />
                  <Button type="button" size="sm" variant="outline" onClick={autoFillPrice} title="Recopier dans le prix client (transport + douane + marge)" data-testid="dp-autoprice-btn">🎯 Prix auto</Button>
                </div>
                {recomputedPrice > 0 && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Prix recommandé : <strong>{recomputedPrice.toFixed(2)} €</strong> ({(parseFloat(unitCost)||0).toFixed(2)} transport + {(parseFloat(customsFee)||0).toFixed(2)} 🛃 + {(parseFloat(ismMargin)||0).toFixed(2)} 💼)
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-slate-600 mt-1 italic">
              Le prix client final inclut tout : transport + douane + marge ISM. Le client ne voit qu&apos;un seul prix.
              Le calculateur 🧮 estime le coût transport par unité. Bouton 🎯 pour recopier le prix recommandé.
            </p>
            <Button className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={attach} disabled={attaching} data-testid="dp-attach-btn">
              {attaching ? <Loader2 className="animate-spin w-4 h-4" /> : <><Plus className="w-4 h-4 mr-1" /> Attacher</>}
            </Button>
          </div>

          {/* List */}
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {dps.length === 0 && <div className="text-center text-slate-500 py-6">Aucun produit dans ce deal</div>}
            {dps.map(dp => {
              const currentMargin = dp.current_margin_per_unit;
              return (
              <div key={dp.id} className="border rounded p-3 flex items-center gap-3">
                {dp.product?.photos?.[0] && <img src={dp.product.photos[0]} alt="" className="w-14 h-14 rounded object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{dp.product?.title}</div>
                  <div className="text-xs text-slate-500 truncate">
                    Prix : <strong>{dp.current_price?.toFixed(2)} €</strong>
                    {dp.pricing_mode === 'tiers' && <span className="ml-1 text-purple-600">(paliers)</span>}
                    {currentMargin !== null && currentMargin !== undefined && (
                      <span className={currentMargin >= 0 ? 'ml-2 text-green-700 font-semibold' : 'ml-2 text-red-600 font-semibold'}>
                        · Marge {currentMargin >= 0 ? '+' : ''}{currentMargin.toFixed(2)}€/u {currentMargin < 0 && '🚨'}
                      </span>
                    )}
                    {dp.pricing_mode !== 'tiers' && dp.break_even_orders > 0 && (
                      <span className={dp.is_profitable ? 'ml-2 text-green-700' : 'ml-2 text-orange-600'}>
                        · {dp.is_profitable ? '💰 rentable' : `💸 ${dp.received_orders_count}/${dp.break_even_orders} pour rentabilité`}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    👀 {dp.interested_count} · 🛒 {dp.declared_orders_count} · <span className="text-green-700 font-semibold">📦 {dp.received_orders_count}</span>
                    {dp.customs_fee > 0 && (
                      <span className="ml-2 text-slate-600">· 🛃 {dp.customs_fee.toFixed(2)}€/u</span>
                    )}
                    {dp.ism_margin > 0 && (
                      <span className="ml-2 text-indigo-700 font-semibold">· 💼 marge cible {dp.ism_margin.toFixed(2)}€/u</span>
                    )}
                    {dp.min_orders_to_ship > 0 && (
                      <span className={dp.is_viable ? 'ml-2 text-green-700' : 'ml-2 text-orange-600 font-semibold'}>
                        · {dp.is_viable ? '✅ prêt à partir' : `⏳ ${dp.received_orders_count}/${dp.min_orders_to_ship} min. expédition`}
                      </span>
                    )}
                  </div>
                </div>
                <Button size="sm" variant={dp.featured ? 'default' : 'outline'} onClick={() => toggleFeatured(dp)} title="À la une">⭐</Button>
                <Button size="sm" variant="outline" onClick={() => editPrice(dp)} title="Prix + rentabilité">💰</Button>
                <Button size="sm" variant="outline" onClick={() => editTiers(dp)} title="Passer en paliers (avancé)">📊</Button>
                <Button size="sm" variant="outline" onClick={() => editSafety(dp)} title="Seuil min. + coût">🛡️</Button>
                <Button size="sm" variant="outline" onClick={() => detach(dp.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
              );
            })}
          </div>
        </>
      )}
      {calcOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={() => setCalcOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{fontFamily:'Chivo, sans-serif'}}>🧮 Calculateur coût unitaire</h3>
              <button onClick={() => setCalcOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Estime le coût par unité en fonction de la place prise dans la palette (volume ET poids).
              La contrainte la plus serrée détermine combien d&apos;unités rentrent.
            </p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold text-slate-700">📦 Dimensions du carton</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  <Input type="number" step="0.1" placeholder="L (cm)" value={calcForm.item_length_cm} onChange={e => setCalcForm({...calcForm, item_length_cm: e.target.value})} data-testid="calc-item-L" />
                  <Input type="number" step="0.1" placeholder="l (cm)" value={calcForm.item_width_cm} onChange={e => setCalcForm({...calcForm, item_width_cm: e.target.value})} data-testid="calc-item-W" />
                  <Input type="number" step="0.1" placeholder="H (cm)" value={calcForm.item_height_cm} onChange={e => setCalcForm({...calcForm, item_height_cm: e.target.value})} data-testid="calc-item-H" />
                  <Input type="number" step="0.01" placeholder="Poids (kg)" value={calcForm.item_weight_kg} onChange={e => setCalcForm({...calcForm, item_weight_kg: e.target.value})} data-testid="calc-item-KG" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">🪵 Palette utilisée</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  <Input type="number" step="0.1" placeholder="L" value={calcForm.pallet_length_cm} onChange={e => setCalcForm({...calcForm, pallet_length_cm: e.target.value})} />
                  <Input type="number" step="0.1" placeholder="l" value={calcForm.pallet_width_cm} onChange={e => setCalcForm({...calcForm, pallet_width_cm: e.target.value})} />
                  <Input type="number" step="0.1" placeholder="H max" value={calcForm.pallet_max_height_cm} onChange={e => setCalcForm({...calcForm, pallet_max_height_cm: e.target.value})} />
                  <Input type="number" step="0.1" placeholder="Poids max" value={calcForm.pallet_max_weight_kg} onChange={e => setCalcForm({...calcForm, pallet_max_weight_kg: e.target.value})} />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Défaut : europalette 120×80×180 cm, 600 kg max</p>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">💰 Coût total de la palette (€)</Label>
                <Input type="number" step="0.1" placeholder="ex: 450" value={calcForm.pallet_total_cost} onChange={e => setCalcForm({...calcForm, pallet_total_cost: e.target.value})} data-testid="calc-total-cost" />
                <p className="text-[10px] text-slate-500 mt-1">Transport + manutention + votre marge cible</p>
              </div>
              <Button onClick={runCalc} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="calc-run">Calculer</Button>

              {calcResult && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4">
                  <div className="text-3xl font-black text-blue-900 mb-2">{calcResult.cost_per_item?.toFixed(2)} €<span className="text-sm font-normal text-blue-700"> / unité</span></div>
                  <div className="text-sm text-slate-700 mb-3">{calcResult.hint}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>Volume unité : <strong>{calcResult.item_volume_m3} m³</strong></div>
                    <div>Volume palette : <strong>{calcResult.pallet_volume_m3} m³</strong></div>
                    <div>Par volume : <strong>{calcResult.items_by_volume} unités</strong></div>
                    <div>Par poids : <strong>{calcResult.items_by_weight} unités</strong></div>
                    <div>Poids réel : <strong>{calcForm.item_weight_kg} kg</strong></div>
                    <div>Poids volumétrique : <strong>{calcResult.volumetric_weight_kg} kg</strong></div>
                    <div className="col-span-2 pt-2 border-t border-blue-200 text-blue-900">
                      Contrainte limitante : <strong>{calcResult.limiting_factor_label || calcResult.limiting_factor}</strong>
                    </div>
                  </div>
                  <Button onClick={applyCalcToInput} className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm">Utiliser ce coût ({calcResult.cost_per_item?.toFixed(2)} €)</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
function ProductsTab({ meta }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await adminDealsAPI.listProducts(); setProducts(data); } catch { toast.error('Erreur'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => { try { await adminDealsAPI.duplicateProduct(id); toast.success('Dupliqué'); load(); } catch (e) { toast.error(e.response?.data?.detail); } };
  const remove = async (id) => { if (!window.confirm('Archiver ?')) return; try { await adminDealsAPI.deleteProduct(id); load(); } catch (e) { toast.error(e.response?.data?.detail); } };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Catalogue produits</h3>
        <Button onClick={() => setEditing({})} className="bg-amber-500 hover:bg-amber-600 text-slate-900" size="sm" data-testid="products-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau produit</Button>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map(p => (
            <div key={p.id} className={`border rounded-lg p-3 ${p.active === false ? 'opacity-50' : ''}`} data-testid={`product-row-${p.slug}`}>
              <div className="flex gap-3">
                {p.photos?.[0] ? <img src={p.photos[0]} alt="" className="w-16 h-16 rounded object-cover" /> : <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center"><Package className="text-slate-300" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500">{p.brand}</div>
                  <div className="text-sm font-semibold truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.category} · {p.merchant_price?.toFixed(2)} €</div>
                </div>
              </div>
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="flex-1"><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(p.id)}><Copy className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                {p.merchant_url && <a href={p.merchant_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5" /></Button></a>}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <ProductForm value={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductForm({ value, meta, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: value.title || '', brand: value.brand || '', reference: value.reference || '',
    category: value.category || 'autre', description: value.description || '',
    photos: value.photos || [],
    merchant_name: value.merchant_name || '', merchant_url: value.merchant_url || '',
    merchant_price: value.merchant_price ?? '',
    est_length_cm: value.est_length_cm ?? '', est_width_cm: value.est_width_cm ?? '',
    est_height_cm: value.est_height_cm ?? '', est_weight_kg: value.est_weight_kg ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [autofillUrl, setAutofillUrl] = useState('');
  const [autofilling, setAutofilling] = useState(false);
  const [autofillMsg, setAutofillMsg] = useState('');
  const [newPhoto, setNewPhoto] = useState('');

  const runAutofill = async () => {
    if (!autofillUrl.trim()) return toast.error('Collez une URL produit');
    setAutofilling(true); setAutofillMsg('');
    try {
      const { data } = await adminDealsAPI.fetchFromUrl(autofillUrl.trim());
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        brand: data.brand || f.brand,
        description: data.description || f.description,
        category: data.category || f.category,
        merchant_name: data.merchant_name || f.merchant_name,
        merchant_url: data.merchant_url || autofillUrl.trim(),
        merchant_price: data.merchant_price ?? f.merchant_price,
        photos: (data.photos && data.photos.length) ? data.photos : f.photos,
        est_length_cm: data.est_length_cm ?? f.est_length_cm,
        est_width_cm: data.est_width_cm ?? f.est_width_cm,
        est_height_cm: data.est_height_cm ?? f.est_height_cm,
        est_weight_kg: data.est_weight_kg ?? f.est_weight_kg,
      }));
      const src = data.source || 'llm';
      const blocked = data.scrape_blocked;
      setAutofillMsg(
        blocked
          ? `⚠️ Site bloqué — infos devinées via IA depuis l'URL. Vérifiez/complétez les champs.`
          : `✅ Rempli automatiquement (source : ${src}). Vérifiez les valeurs.`
      );
      toast.success('Formulaire rempli');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'analyser cette URL');
    }
    setAutofilling(false);
  };

  const addPhoto = () => {
    const url = newPhoto.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) return toast.error('URL doit commencer par http(s)://');
    if (form.photos.includes(url)) return toast.error('Photo déjà ajoutée');
    setForm(f => ({ ...f, photos: [...f.photos, url] }));
    setNewPhoto('');
  };
  const removePhoto = (i) => setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const backend = process.env.REACT_APP_BACKEND_URL;
    for (const f of files) {
      try {
        const { data } = await adminDealsAPI.uploadPhoto(f);
        // Prefix relative URL with backend origin so <img> works from any page
        const full = data.url.startsWith('http') ? data.url : `${backend}${data.url}`;
        setForm(fm => ({ ...fm, photos: [...fm.photos, full] }));
        toast.success(`${f.name} envoyée`);
      } catch (e) {
        toast.error(`${f.name} : ${e.response?.data?.detail || 'échec upload'}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form,
        photos: form.photos.filter(Boolean),
        merchant_price: form.merchant_price ? parseFloat(form.merchant_price) : null,
        est_length_cm: form.est_length_cm ? parseFloat(form.est_length_cm) : null,
        est_width_cm: form.est_width_cm ? parseFloat(form.est_width_cm) : null,
        est_height_cm: form.est_height_cm ? parseFloat(form.est_height_cm) : null,
        est_weight_kg: form.est_weight_kg ? parseFloat(form.est_weight_kg) : null,
      };
      if (value.id) await adminDealsAPI.updateProduct(value.id, payload);
      else await adminDealsAPI.createProduct(payload);
      toast.success('Enregistré'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title={value.id ? 'Modifier produit' : 'Nouveau produit'} testId="product-form" wide>
      <form onSubmit={submit} className="space-y-3">
        {/* AUTO-REMPLISSAGE PAR URL */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">🪄</span>
            <span className="text-sm font-bold text-indigo-900">Auto-remplir depuis un lien produit</span>
          </div>
          <div className="flex gap-2">
            <Input
              value={autofillUrl}
              onChange={e => setAutofillUrl(e.target.value)}
              placeholder="Collez l'URL Amazon, Fnac, Nike, etc."
              className="flex-1 text-xs"
              data-testid="prod-autofill-url"
            />
            <Button type="button" onClick={runAutofill} disabled={autofilling} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="prod-autofill-btn">
              {autofilling ? <Loader2 className="animate-spin w-4 h-4" /> : '🪄 Remplir'}
            </Button>
          </div>
          {autofillMsg && <p className="text-xs mt-2 text-indigo-800">{autofillMsg}</p>}
          <p className="text-[11px] text-slate-500 mt-1 italic">L&apos;IA devine titre, marque, prix, dimensions et poids même si le site bloque le scraping.</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><Label>Marque</Label><Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} data-testid="prod-brand" /></div>
          <div><Label>Référence</Label><Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} data-testid="prod-ref" /></div>
        </div>
        <div><Label>Titre *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="prod-title" /></div>
        <div><Label>Catégorie</Label>
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 border rounded-md" data-testid="prod-cat">
            {meta.categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
        <div><Label>Description</Label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full px-3 py-2 border rounded-md" /></div>

        {/* PHOTOS */}
        <div>
          <Label>Photos ({form.photos.length})</Label>

          {/* Upload fichier depuis l'ordinateur */}
          <div className="flex flex-wrap gap-2 mb-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={e => uploadFiles(e.target.files)}
              data-testid="prod-photo-file-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="prod-photo-upload-btn"
            >
              {uploading ? <Loader2 className="animate-spin w-4 h-4 mr-1" /> : '📤'} Choisir des photos (jpg/png)
            </Button>
            <span className="text-xs text-slate-500 self-center italic">ou coller une URL image ci-dessous</span>
          </div>

          {/* Coller URL */}
          <div className="flex gap-2 mb-2">
            <Input
              value={newPhoto}
              onChange={e => setNewPhoto(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhoto(); } }}
              placeholder="https://…"
              className="flex-1 font-mono text-xs"
              data-testid="prod-photo-input"
            />
            <Button type="button" size="sm" variant="outline" onClick={addPhoto} data-testid="prod-photo-add">➕ Ajouter URL</Button>
          </div>

          {form.photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {form.photos.map((p, i) => (
                <PhotoThumb key={p} url={p} index={i} onRemove={() => removePhoto(i)} />
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div><Label>Marchand</Label><Input value={form.merchant_name} onChange={e => setForm({...form, merchant_name: e.target.value})} placeholder="Amazon, Darty…" /></div>
          <div><Label>Prix marchand (€)</Label><Input type="number" step="0.01" value={form.merchant_price} onChange={e => setForm({...form, merchant_price: e.target.value})} /></div>
        </div>
        <div><Label>URL marchand</Label><Input value={form.merchant_url} onChange={e => setForm({...form, merchant_url: e.target.value})} placeholder="https://…" className="font-mono text-xs" data-testid="prod-url" /></div>
        <div className="grid grid-cols-4 gap-2">
          <div><Label>L (cm)</Label><Input type="number" step="0.1" value={form.est_length_cm} onChange={e => setForm({...form, est_length_cm: e.target.value})} /></div>
          <div><Label>l (cm)</Label><Input type="number" step="0.1" value={form.est_width_cm} onChange={e => setForm({...form, est_width_cm: e.target.value})} /></div>
          <div><Label>H (cm)</Label><Input type="number" step="0.1" value={form.est_height_cm} onChange={e => setForm({...form, est_height_cm: e.target.value})} /></div>
          <div><Label>Poids (kg)</Label><Input type="number" step="0.01" value={form.est_weight_kg} onChange={e => setForm({...form, est_weight_kg: e.target.value})} /></div>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="prod-submit">
          {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Enregistrer'}
        </Button>
      </form>
    </Modal>
  );
}

// -------------------- ORDERS TAB --------------------
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [receiving, setReceiving] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {};
      const effectiveStatus = 'status' in overrides ? overrides.status : status;
      const effectiveCn = 'customer_number' in overrides ? overrides.customer_number : customerNumber.trim();
      if (effectiveStatus) params.status = effectiveStatus;
      if (effectiveCn) params.customer_number = effectiveCn;
      const { data } = await adminDealsAPI.listOrders(params);
      setOrders(data);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [status]);

  const setStatusFor = async (id, newStatus) => {
    try { await adminDealsAPI.updateOrderStatus(id, { status: newStatus }); toast.success('Statut mis à jour'); load(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-bold">Réceptions</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={customerNumber}
            onChange={e => setCustomerNumber(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="🔎 n° client CB-..."
            className="w-44 text-sm"
            data-testid="orders-search-cb"
          />
          <Button size="sm" variant="outline" onClick={load} data-testid="orders-search-btn">Rechercher</Button>
          {customerNumber && (
            <Button size="sm" variant="ghost" onClick={() => { setCustomerNumber(''); load({ customer_number: '' }); }} data-testid="orders-search-clear">✕</Button>
          )}
          <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
            <option value="">Tous statuts</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : orders.length === 0 ? (
        <div className="text-center text-slate-500 py-8">
          {customerNumber ? `Aucune commande pour ${customerNumber}` : 'Aucune commande'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-2">Client</th><th className="text-left p-2">Produit</th>
                <th className="text-left p-2">Deal</th><th className="text-left p-2">💳 Payé</th>
                <th className="text-left p-2">Marchand #</th>
                <th className="text-left p-2">Statut</th><th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="border-t" data-testid={`order-row-${o.id.slice(0,8)}`}>
                  <td className="p-2">
                    <div className="font-semibold">{o.first_name} {o.last_name}</div>
                    <div className="text-xs text-slate-500">{o.email} · <strong>{o.customer_number || '—'}</strong></div>
                  </td>
                  <td className="p-2 text-xs">{o.product?.brand} — {o.product?.title}</td>
                  <td className="p-2 text-xs">{o.deal?.name}</td>
                  <td className="p-2 text-xs">
                    {o.paid ? (
                      <span className="text-green-700 font-bold">✓ {(o.amount_paid ?? o.amount_expected ?? 0).toFixed(2)}€</span>
                    ) : o.amount_expected ? (
                      <span className="text-orange-600 font-semibold">⏳ {o.amount_expected.toFixed(2)}€</span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-2 text-xs">
                    <div>{o.merchant_order_number}</div>
                    {o.tracking_number && <div className="text-slate-500">🚚 {o.tracking_number}</div>}
                  </td>
                  <td className="p-2"><Badge className={STATUS_COLORS[o.status]}>{STATUS_LABELS[o.status] || o.status}</Badge></td>
                  <td className="p-2">
                    <div className="flex gap-1 flex-wrap">
                      {o.status === 'declared' && <>
                        <Button size="sm" variant="outline" onClick={() => setStatusFor(o.id, 'in_transit')}>Transit</Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setReceiving(o)} data-testid={`order-receive-${o.id.slice(0,8)}`}>Reçue</Button>
                      </>}
                      {o.status === 'in_transit' && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setReceiving(o)}>Reçue</Button>}
                      {o.status === 'received' && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setAssigning(o)}>Affecter palette</Button>}
                      {o.status !== 'cancelled' && o.status !== 'delivered' && o.status !== 'pending_payment' && <Button size="sm" variant="outline" onClick={() => setStatusFor(o.id, 'cancelled')}>Annuler</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {receiving && <ReceiveForm order={receiving} onClose={() => setReceiving(null)} onSaved={() => { setReceiving(null); load(); }} />}
      {assigning && <AssignForm order={assigning} onClose={() => setAssigning(null)} onSaved={() => { setAssigning(null); load(); }} />}
    </div>
  );
}

function ReceiveForm({ order, onClose, onSaved }) {
  const p = order.product;
  const [form, setForm] = useState({
    real_length_cm: p?.est_length_cm ?? '', real_width_cm: p?.est_width_cm ?? '',
    real_height_cm: p?.est_height_cm ?? '', real_weight_kg: p?.est_weight_kg ?? '',
    admin_notes: '',
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        real_length_cm: parseFloat(form.real_length_cm),
        real_width_cm: parseFloat(form.real_width_cm),
        real_height_cm: parseFloat(form.real_height_cm),
        real_weight_kg: parseFloat(form.real_weight_kg),
        admin_notes: form.admin_notes,
      };
      await adminDealsAPI.receiveOrder(order.id, payload);
      toast.success('Reçue — compteur mis à jour, palier recalculé'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title="Réception — dimensions réelles" testId="receive-form">
      <div className="text-sm mb-3 bg-slate-50 rounded p-3">
        <strong>{p?.brand} — {p?.title}</strong><br/>
        <span className="text-xs">Client : {order.first_name} {order.last_name} — Marchand #{order.merchant_order_number}</span>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          <div><Label>L (cm) *</Label><Input type="number" step="0.1" value={form.real_length_cm} onChange={e => setForm({...form, real_length_cm: e.target.value})} required data-testid="recv-length" /></div>
          <div><Label>l (cm) *</Label><Input type="number" step="0.1" value={form.real_width_cm} onChange={e => setForm({...form, real_width_cm: e.target.value})} required data-testid="recv-width" /></div>
          <div><Label>H (cm) *</Label><Input type="number" step="0.1" value={form.real_height_cm} onChange={e => setForm({...form, real_height_cm: e.target.value})} required data-testid="recv-height" /></div>
          <div><Label>Poids (kg) *</Label><Input type="number" step="0.01" value={form.real_weight_kg} onChange={e => setForm({...form, real_weight_kg: e.target.value})} required data-testid="recv-weight" /></div>
        </div>
        <div><Label>Notes admin</Label><textarea value={form.admin_notes} onChange={e => setForm({...form, admin_notes: e.target.value})} rows={2} className="w-full px-3 py-2 border rounded-md" /></div>
        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
          Volume calculé : <strong>{form.real_length_cm && form.real_width_cm && form.real_height_cm ? ((parseFloat(form.real_length_cm) * parseFloat(form.real_width_cm) * parseFloat(form.real_height_cm)) / 1_000_000).toFixed(3) : '—'} m³</strong>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700" data-testid="recv-submit">
          {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Marquer reçue'}
        </Button>
      </form>
    </Modal>
  );
}

function AssignForm({ order, onClose, onSaved }) {
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { adminDealsAPI.listPallets(order.deal_id).then(r => { setPallets(r.data); setLoading(false); }); }, [order.deal_id]);

  const assign = async (palletId) => {
    try { await adminDealsAPI.assignOrder(palletId, order.id); toast.success('Affectée'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail); }
  };
  const createAndAssign = async () => {
    try {
      const { data: pal } = await adminDealsAPI.createPallet({ deal_id: order.deal_id });
      await adminDealsAPI.assignOrder(pal.id, order.id);
      toast.success('Nouvelle palette créée et affectée'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail); }
  };

  const itemVol = ((order.real_length_cm || 0) * (order.real_width_cm || 0) * (order.real_height_cm || 0)) / 1_000_000;

  return (
    <Modal onClose={onClose} title="Affecter à une palette" testId="assign-form">
      <div className="text-sm bg-slate-50 rounded p-3 mb-3">
        Colis : <strong>{itemVol.toFixed(3)} m³ · {order.real_weight_kg} kg</strong>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {pallets.length === 0 && <div className="text-center text-slate-500 py-4">Aucune palette pour ce deal</div>}
          {pallets.map(p => {
            const fill = p.fill || {};
            const canFit = (fill.volume_used_m3 + itemVol) <= (fill.volume_capacity_m3 + 0.001) && (fill.weight_used_kg + (order.real_weight_kg || 0)) <= fill.weight_capacity_kg;
            return (
              <div key={p.id} className={`border rounded p-3 ${canFit ? 'border-green-300 bg-green-50' : 'border-red-200 bg-red-50 opacity-70'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">Palette {p.number}</div>
                    <div className="text-xs text-slate-500">
                      Vol : {fill.volume_used_m3?.toFixed(3)}/{fill.volume_capacity_m3?.toFixed(3)} m³ ({fill.volume_percent}%) · Poids : {fill.weight_used_kg}/{fill.weight_capacity_kg} kg ({fill.weight_percent}%)
                    </div>
                  </div>
                  <Button size="sm" disabled={!canFit} onClick={() => assign(p.id)} data-testid={`assign-to-${p.number}`}>{canFit ? 'Affecter' : 'Trop plein'}</Button>
                </div>
              </div>
            );
          })}
          <Button variant="outline" className="w-full" onClick={createAndAssign} data-testid="assign-new-pallet"><Plus className="w-4 h-4 mr-1" /> Créer une nouvelle palette</Button>
        </div>
      )}
    </Modal>
  );
}

// -------------------- PALLETS TAB --------------------
function PalletsTab() {
  const [pallets, setPallets] = useState([]);
  const [deals, setDeals] = useState([]);
  const [dealFilter, setDealFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([adminDealsAPI.listPallets(dealFilter || null), adminDealsAPI.listDeals()]);
      setPallets(p.data); setDeals(d.data);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [dealFilter]);

  const remove = async (id) => { if (!window.confirm('Supprimer palette ?')) return; try { await adminDealsAPI.deletePallet(id); load(); } catch (e) { toast.error(e.response?.data?.detail); } };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Palettes</h3>
        <div className="flex gap-2">
          <select value={dealFilter} onChange={e => setDealFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
            <option value="">Tous deals</option>
            {deals.map(d => <option key={d.id} value={d.id}>{d.destination?.flag} {d.name}</option>)}
          </select>
          <Button onClick={() => setCreating(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" size="sm" data-testid="pallets-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle palette</Button>
        </div>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pallets.map(p => {
            const f = p.fill || {};
            const deal = deals.find(d => d.id === p.deal_id);
            return (
              <div key={p.id} className="border rounded-lg p-4" data-testid={`pallet-${p.number}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-bold text-lg">Palette {p.number}</div>
                    <div className="text-xs text-slate-500">{deal?.destination?.flag} {deal?.name}</div>
                  </div>
                  <Badge className={p.status === 'shipped' ? 'bg-purple-500' : p.status === 'full' ? 'bg-red-500' : 'bg-green-500'}>{p.status}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-xs text-slate-500">Volume</div>
                    <div className="font-semibold">{f.volume_used_m3?.toFixed(3)} / {f.volume_capacity_m3?.toFixed(3)} m³</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full mt-1"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, f.volume_percent || 0)}%` }} /></div>
                    <div className="text-xs text-slate-500 mt-0.5">{f.volume_percent}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Poids</div>
                    <div className="font-semibold">{f.weight_used_kg} / {f.weight_capacity_kg} kg</div>
                    <div className="w-full h-2 bg-slate-100 rounded-full mt-1"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, f.weight_percent || 0)}%` }} /></div>
                    <div className="text-xs text-slate-500 mt-0.5">{f.weight_percent}%</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mb-2">📦 {f.items_count} article(s) · Dim {p.length_cm}×{p.width_cm}×{p.max_height_cm} cm</div>
                <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => remove(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button></div>
              </div>
            );
          })}
          {pallets.length === 0 && <div className="col-span-2 text-center text-slate-500 py-8">Aucune palette</div>}
        </div>
      )}
      {creating && <PalletCreateForm deals={deals} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function PalletCreateForm({ deals, onClose, onSaved }) {
  const [form, setForm] = useState({ deal_id: deals[0]?.id || '', length_cm: 120, width_cm: 80, max_height_cm: 180, max_weight_kg: 600 });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await adminDealsAPI.createPallet({ ...form,
        length_cm: parseFloat(form.length_cm), width_cm: parseFloat(form.width_cm),
        max_height_cm: parseFloat(form.max_height_cm), max_weight_kg: parseFloat(form.max_weight_kg),
      });
      toast.success('Palette créée'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title="Nouvelle palette" testId="pallet-form">
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Deal *</Label>
          <select value={form.deal_id} onChange={e => setForm({...form, deal_id: e.target.value})} className="w-full px-3 py-2 border rounded-md" required data-testid="pallet-deal">
            {deals.map(d => <option key={d.id} value={d.id}>{d.destination?.flag} {d.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>L (cm)</Label><Input type="number" step="0.1" value={form.length_cm} onChange={e => setForm({...form, length_cm: e.target.value})} /></div>
          <div><Label>l (cm)</Label><Input type="number" step="0.1" value={form.width_cm} onChange={e => setForm({...form, width_cm: e.target.value})} /></div>
          <div><Label>H max (cm)</Label><Input type="number" step="0.1" value={form.max_height_cm} onChange={e => setForm({...form, max_height_cm: e.target.value})} /></div>
          <div><Label>Poids max (kg)</Label><Input type="number" step="0.1" value={form.max_weight_kg} onChange={e => setForm({...form, max_weight_kg: e.target.value})} /></div>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="pallet-submit">
          {loading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Créer'}
        </Button>
      </form>
    </Modal>
  );
}

// -------------------- CATEGORIES TAB --------------------
function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');

  const load = async () => { setLoading(true); try { const { data } = await adminDealsAPI.listCategories(); setCats(data); } catch { toast.error('Erreur'); } setLoading(false); };
  useEffect(() => { load(); }, []);

  const create = async () => { if (!newLabel) return; try { await adminDealsAPI.createCategory({ label: newLabel }); setNewLabel(''); load(); } catch (e) { toast.error(e.response?.data?.detail); } };
  const toggle = async (c) => { try { await adminDealsAPI.updateCategory(c.id, { active: !c.active }); load(); } catch (e) { toast.error(e.response?.data?.detail); } };
  const rename = async (c) => { const label = prompt('Nouveau nom', c.label); if (!label) return; try { await adminDealsAPI.updateCategory(c.id, { label }); load(); } catch (e) { toast.error(e.response?.data?.detail); } };

  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-bold mb-4">Catégories</h3>
      <div className="flex gap-2 mb-4">
        <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Nouvelle catégorie" data-testid="cat-new-input" />
        <Button onClick={create} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="cat-new-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {cats.map(c => (
            <div key={c.id} className={`border rounded p-2 flex items-center justify-between ${c.active ? '' : 'opacity-50'}`}>
              <span className="text-sm">{c.label}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => rename(c)}><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(c)}>{c.active ? '👁️' : '🚫'}</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------- WAITLIST TAB --------------------
function WaitlistTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { adminDealsAPI.listWaitlist().then(r => { setRows(r.data); setLoading(false); }); }, []);
  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="text-lg font-bold mb-4">Liste d&apos;attente</h3>
      {loading ? <Loader2 className="animate-spin" /> : rows.length === 0 ? <p className="text-slate-500">Aucune demande</p> : (
        <table className="w-full text-sm"><thead className="bg-slate-50 text-xs uppercase"><tr><th className="text-left p-2">Email</th><th className="text-left p-2">Prénom</th><th className="text-left p-2">Destination</th><th className="text-left p-2">Date</th></tr></thead>
          <tbody>{rows.map(r => <tr key={r.id} className="border-t"><td className="p-2">{r.email}</td><td className="p-2">{r.first_name}</td><td className="p-2">{r.destination_code}</td><td className="p-2 text-xs">{new Date(r.created_at).toLocaleDateString('fr-FR')}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

// -------------------- SHARED MODAL --------------------
function Modal({ onClose, title, children, testId, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} data-testid={testId}>
      <div className={`bg-white rounded-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} p-6 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-bold" style={{fontFamily: 'Chivo, sans-serif'}}>{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid={`${testId}-close`}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
